import { dequal as deepEqual } from 'dequal/lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { ConfigOverride } from '#dn-types/host.ts';
import { usePrevious } from '#hooks/usePrevious.ts';
import type { HostFormData, HostFormTransformedData } from '#screens/hosts/form/host-form-schema.ts';
import { mapOverridesToForm } from '../advanced/mappers/mapOverridesToForm.ts';

/**
 * This hook is used in forms where we need to track the saved state without setting it as the defaultValue of the form.
 * So for example, in the host config overrides, we want the default values to be the un-overridden default (empty state)
 * so that we can show "Reset to default" buttons for each field that has been changed.
 *
 * This means we need a custom "isDirty" to track when the user has changed overrides from their last-saved values, rather than
 * against the defaultValues of the form.
 */
export function useSavedHostOverrides(
  formContext: UseFormReturn<HostFormData, unknown, HostFormTransformedData>,
  /** default values are used to avoid overwriting tag-provided config overrides */
  defaultValues: HostFormData,
  /** configOverrides will be undefined if we are creating a new host. */
  configOverrides?: ConfigOverride[]
) {
  const isEditing = typeof configOverrides !== 'undefined';
  const { getValues, reset } = formContext;
  const savedOverrides = useMemo(() => {
    return mapOverridesToForm(configOverrides ?? [], defaultValues.configOverrides);
  }, [configOverrides, defaultValues]);
  const previousOverrides = usePrevious(savedOverrides);
  const currentValues = getValues();

  // We need to prevent the form from rendering until after we've set up the saved overrides.  Otherwise there can be a race in RHF.
  const [isReady, setIsReady] = useState(false);

  // Reset the form to whatever we got most recently from the server.
  // But we need to be careful not to overwrite tag-provided defaults
  const resetToSaved = useCallback(() => {
    const currVal = getValues();
    reset(
      {
        ...currVal,
        configOverrides: savedOverrides,
      },
      { keepDefaultValues: true, keepDirtyValues: true }
    );
  }, [getValues, reset, savedOverrides]);

  // If we get data from the server, reset the form to show it
  useEffect(() => {
    if (!deepEqual(savedOverrides, previousOverrides)) {
      resetToSaved();
      // HACK: When editing a host, we start off with the saved values, and the reset
      // happens too fast for react-hook-form to update the fieldArrays. This small timeout
      // forces it to happen on the next tick, which seems to solve the issue.
      // eslint-disable-next-line @eslint-react/web-api/no-leaked-timeout
      setTimeout(() => {
        setIsReady(true);
      }, 1);
    }
  }, [resetToSaved, previousOverrides, savedOverrides]);

  /**
   * Compare current values against what's saved on the server.
   * Can't use default `isDirty` from RHF because that compares to default values,
   * And the defaults are always empty in this case, so we can "unset" values.
   */
  const hasDirtyOverrides = !deepEqual(
    currentValues.configOverrides,
    isEditing ? savedOverrides : defaultValues.configOverrides
  );

  return { hasDirtyOverrides, savedOverrides, isSavedOverridesReady: isReady };
}
